/**
 * Contratos das Cotações da praça (Mercado PR 2) — o que a API devolve, campo a
 * campo, e a aritmética de semana ISO que os dois lados precisam.
 *
 * Arquivo próprio (e não `shared/mercado.ts`) porque o vocabulário é outro: lá é
 * romaneio, corretor e compartilhamento; aqui é praça, produto e preço. E porque
 * `shared/mercado.ts` é a fonte da verdade do que sai da fazenda pelo LINK — não
 * misturar dado público de preço com o contrato daquele link.
 *
 * ⚠️ Números chegam prontos. Total, média ponderada, delta e variação do período
 * são calculados NO SERVIDOR; o cliente formata e nada mais. Uma média ponderada
 * refeita na tela diverge da do servidor na primeira venda com kg fracionado.
 */

import { diaSemanaIso, semanaIso } from "./boletim-periodo.js";
import { somarDiasCivis } from "./data-fazenda.js";

export type PeriodoHistorico = "8s" | "3m" | "safra";

export const PERIODOS_HISTORICO: PeriodoHistorico[] = ["8s", "3m", "safra"];

export function ehPeriodoHistorico(v: unknown): v is PeriodoHistorico {
  return typeof v === "string" && PERIODOS_HISTORICO.indexOf(v as PeriodoHistorico) >= 0;
}

// ─── Semana ISO ──────────────────────────────────────────────────────────────
//
// Segunda a domingo, a MESMA definição que "A receber" (PR 1b) adotou em
// `janelaDaSemana`. Data civil pura, sem `Date` local e sem fuso: o servidor
// roda em UTC e o Mac do Hugo em UTC-3, e uma segunda-feira calculada em hora
// local vira domingo antes das 03:00.

/** A segunda-feira da semana ISO que contém `data`. */
export function segundaDaSemanaIso(data: string): string {
  return somarDiasCivis(data, -(diaSemanaIso(data) - 1));
}

/**
 * O rótulo da semana ISO: "2026-W37".
 *
 * ⚠️ O ANO é o ISO, não o do calendário — e é por isso que a função existe em vez
 * de `data.slice(0,4)`. 2027-01-01 é sexta-feira e pertence à semana **53 de
 * 2026**; escrever "2027-W53" ali criaria uma semana que não existe e faria o
 * histórico pular a virada de ano.
 */
export function rotuloSemanaIso(data: string): string {
  const s = semanaIso(data);
  return `${s.ano}-W${String(s.semana).padStart(2, "0")}`;
}

// ─── Público (leitura, sem org) ──────────────────────────────────────────────

export interface PracaDTO {
  slug: string;
  nome: string;
  uf: string;
}

/**
 * As praças de UM comprador — o filtro da tela de Cotações dele.
 *
 * ⚠️ `derivada` diz DE ONDE a lista veio: `false` = ele escolheu em "Minhas
 * praças"; `true` = ninguém escolheu nada e o servidor caiu nas praças dos
 * produtores com quem ele já negocia. A tela precisa da diferença para
 * sugerir que ele confirme, em vez de apresentar um palpite como escolha dele.
 */
export interface MinhasPracasDTO {
  pracas: PracaDTO[];
  derivada: boolean;
}

/**
 * As praças do comprador, cruzadas com o CADASTRO.
 *
 * ⚠️ Slug que não existe mais no cadastro SOME, em silêncio. Praça renomeada ou
 * removida (a 060 apagou um slug) deixaria na lista um filtro que pede uma praça
 * inexistente — e a consulta seguinte responderia 404 numa tela que abriu bem. O
 * comprador não tem o que fazer com essa linha; some é o que ele esperaria.
 *
 * ⚠️ A ORDEM é a do cadastro do comprador, não a do cadastro geral: a primeira
 * da lista é a que a tela abre, e ela precisa ser a que ELE pôs em primeiro.
 *
 * Pura de propósito — a rota é fina, e é aqui que o caso do slug órfão é medido.
 */
export function resolverMinhasPracas(
  slugs: string[],
  cadastro: PracaDTO[],
  derivada: boolean,
): MinhasPracasDTO {
  const porSlug: Record<string, PracaDTO> = {};
  for (const p of cadastro) porSlug[p.slug] = p;
  const pracas: PracaDTO[] = [];
  for (const slug of slugs) {
    const achada = porSlug[slug];
    // Repetido no cadastro do comprador não vira duas linhas iguais no filtro.
    if (achada && pracas.filter(p => p.slug === slug).length === 0) pracas.push(achada);
  }
  return { pracas, derivada };
}

export interface LinhaCotacaoDTO {
  variedade: string;
  qualidade: string | null;
  /** "Prata 1ª" · "Nanica". */
  label: string;
  precoKg: number;
  /**
   * O preço da última semana publicada NA MESMA BASE (referencial) que a de hoje;
   * `null` sem base. O nome ficou de quando era "a semana ISO anterior" — a tela
   * do produtor lê esta chave, e mudar o nome quebraria um contrato para dizer a
   * mesma coisa. A regra está em `anteriorNaMesmaBase`.
   */
  semanaAnterior: number | null;
  /** (atual − anterior) / anterior, em %. `null` sem base — **nunca** `0`. */
  delta: number | null;
  /** A data da publicação contra a qual `delta` foi medido ("vs 31/08"); `null` sem base. */
  comparadoCom: string | null;
}

/**
 * O quadro do dia.
 *
 * Praça sem cotação NÃO é 404: devolve `linhas: []` e `ultimaData` (a última data
 * com publicação, ou `null`). A tela mostra "praça sem cotação publicada hoje"
 * com a data que existe — um 404 viraria tela de erro para um estado normal.
 */
export interface CotacaoHojeDTO {
  praca: string;
  pracaNome: string;
  /**
   * PRODUTOR | VENDA — o que a associação divulgou (059).
   *
   * ⚠️ É esta etiqueta que decide a FORMA da comparação: com PRODUTOR as bases
   * batem e o delta do produtor sai em %; com VENDA sai em R$/kg de diferença
   * para a placa, sem porcentagem e sem juízo de valor. A plataforma nunca
   * escreve "vendeu abaixo da praça" contra uma placa de carga embalada.
   */
  referencial: string;
  /** ISO "AAAA-MM-DD" da cotação exibida, ou `null` se não há nenhuma. */
  data: string | null;
  /** Instante ISO da última gravação daquela data, ou `null`. */
  atualizadoEm: string | null;
  /** A última data COM publicação, mesmo quando anterior a `ate`. */
  ultimaData: string | null;
  /**
   * Até quando aquele preço é considerado fresco (058) — a MENOR validade entre
   * as linhas mostradas. `null` só em quadro vazio.
   *
   * ⚠️ Vencida NÃO é vazia. O preço continua no quadro, com um chip "vencida ·
   * há N dias" ao lado: a última cotação publicada é sempre a válida, e sumir
   * com ela criaria um buraco onde o produtor precisa de um número.
   */
  validaAte: string | null;
  vencida: boolean;
  /** Dias desde o vencimento; 0 quando não vencida. */
  diasVencida: number;
  fonte: string | null;
  linhas: LinhaCotacaoDTO[];
}

export interface SemanaHistoricoDTO {
  /** "2026-W37" — ano ISO, ver `rotuloSemanaIso`. */
  semana: string;
  /** A data da última publicação DAQUELA semana. */
  data: string;
  precoKg: number;
  /**
   * PRODUTOR | VENDA — o que a praça divulgou NAQUELA semana.
   *
   * ⚠️ Por semana, e não uma vez para a série inteira: a associação muda a
   * placa de uma semana para a outra, e a coluna "Praça publicou" existe
   * justamente para que a queda entre duas semanas não seja lida como queda de
   * preço quando foi troca de base.
   */
  referencial: string;
  /**
   * Contra a última semana PUBLICADA NA MESMA BASE — não a anterior do
   * calendário, e não a anterior de outra placa. Ver `anteriorNaMesmaBase`.
   */
  delta: number | null;
  /** A data da publicação contra a qual `delta` foi medido; `null` sem base. */
  comparadoCom: string | null;
}

export interface HistoricoDTO {
  praca: string;
  variedade: string;
  qualidade: string | null;
  label: string;
  periodo: PeriodoHistorico;
  /** Mais recente em cima. */
  semanas: SemanaHistoricoDTO[];
  min: number | null;
  max: number | null;
  /** Da semana mais antiga à mais recente do período, em %. `null` com < 2. */
  variacaoPeriodo: number | null;
}

/** Quantas semanas a matriz do comprador mostra por página ("Ver mais 8 semanas"). */
export const SEMANAS_POR_PAGINA = 8;
/** Teto do `?semanas=` — a matriz nunca pede mais que isso de uma vez. */
export const SEMANAS_MAXIMO = 52;

/** Um produto numa semana da matriz: o preço de fechamento e a variação na mesma base. */
export interface ProdutoDaSemanaDTO {
  variedade: string;
  qualidade: string | null;
  label: string;
  precoKg: number;
  /** PRODUTOR | VENDA — a placa DESTE produto nesta semana. */
  referencial: string;
  /** Contra a última semana publicada na mesma base; `null` sem base. */
  delta: number | null;
  comparadoCom: string | null;
}

export interface SemanaDaPracaDTO {
  /** "2026-W39". */
  semana: string;
  /** A segunda-feira da semana — é o cursor do "Ver mais" (`antesDe`). */
  segunda: string;
  /** A data da última publicação da semana (a mais recente entre os produtos). */
  data: string;
  /** A placa da publicação mais recente da semana. */
  referencial: string;
  /** Na ordem de `PRODUTOS`; produto que a praça não cotou na semana fica de fora. */
  produtos: ProdutoDaSemanaDTO[];
}

/**
 * O histórico da praça com TODOS os produtos — a matriz "Semana a semana" do
 * comprador. Mais recente em cima; `temMais` diz se existe semana publicada
 * antes da mais antiga devolvida.
 */
export interface HistoricoDaPracaDTO {
  praca: string;
  semanas: SemanaDaPracaDTO[];
  temMais: boolean;
}

// ─── Comprador (autenticado, filtrado por org_id) ────────────────────────────

/** O que o comprador pagou por um produto numa semana — média PONDERADA por kg. */
export interface PagouProdutoDTO {
  variedade: string;
  qualidade: string | null;
  kg: number;
  precoMedioKg: number;
  /** O preço da praça naquela semana para aquele produto; `null` sem publicação. */
  precoPraca: number | null;
  /**
   * `precoMedioKg − precoPraca`, em R$/kg — SÓ em semana ao produtor.
   *
   * ⚠️ `null` com placa de VENDA: aquilo é o preço da caixa na praça, outra
   * base, e a diferença teria unidade de dinheiro e significado nenhum.
   */
  diferenca: number | null;
}

export interface PagouSemanaDTO {
  semana: string;
  produtos: PagouProdutoDTO[];
}

/**
 * "O que você pagou" semana a semana, alinhado às MESMAS semanas da matriz
 * (`semanas` + `antesDe` iguais). Semana sem romaneio não aparece.
 */
export interface PagouDTO {
  semanas: PagouSemanaDTO[];
  /** Itens de romaneio que não casam com produto da praça — contados, nunca somem. */
  itensNaoMapeados: number;
}

// ─── Produtor (autenticado, filtrado por org_id) ─────────────────────────────

export interface MinhaVendaVsPracaDTO {
  variedade: string;
  qualidade: string | null;
  label: string;
  /** Média PONDERADA por kg das vendas EMITIDAS/PAGAS no período; `null` sem venda. */
  meuPreco: number | null;
  /** A cotação do dia daquele produto; `null` sem cotação. */
  precoPraca: number | null;
  /**
   * A comparação, na forma que o referencial da cotação permite: % com placa
   * ao produtor, R$/kg de diferença com placa de venda. `null` quando falta um
   * dos dois preços. Ver `compararComPraca`.
   */
  delta: ComparacaoDTO | null;
  vendas: number;
  kg: number;
}

export interface MinhasVsPracaDTO {
  /**
   * `null` quando não há praça ativa no cadastro — não existe slug de
   * fallback (ver `pracaDaOrg`). A tela mostra o seletor, não um chute.
   */
  praca: string | null;
  pracaNome: string | null;
  periodo: string;
  /** PRODUTOR | VENDA — a etiqueta da cotação usada na comparação (059). */
  referencial: string;
  produtos: MinhaVendaVsPracaDTO[];
  /**
   * Σ kg × (praça − meu), **só as parcelas positivas e só quando o total é > 0**.
   * "Se tivesse vendido na praça" é um argumento, não um saldo: quando dá
   * negativo o produtor vendeu ACIMA da praça e a frase não se aplica.
   *
   * ⚠️ `null` quando o referencial é VENDA. A frase só faz sentido contra uma
   * placa que mede a mesma coisa que o romaneio: "teria recebido mais R$ 3.400
   * vendendo na praça" comparado com preço de caixa embalada seria um número
   * inventado, e desses o produtor negocia em cima.
   */
  seNaPraca: number | null;
  /** Itens de venda sem produto da praça — o rodapé "N itens sem produto mapeado". */
  itensNaoMapeados: number;
}

export interface MinhaSemanaDTO {
  semana: string;
  /** Média ponderada por kg das vendas daquela semana; `null` sem venda. */
  meuPreco: number | null;
  vendas: number;
  kg: number;
  /** A etiqueta da publicação daquela semana; `null` se a praça não publicou. */
  referencial: string | null;
  /** O preço da praça naquela semana (a última publicação dela); `null` sem. */
  precoPraca: number | null;
  /**
   * A diferença da semana, na forma que o referencial dela permite. `null`
   * quando falta o preço da praça ou a venda.
   */
  diferenca: ComparacaoDTO | null;
}

/**
 * A média das diferenças de um período, SEPARADA por referencial.
 *
 * ⚠️ Separada, nunca somada. Misturar semanas de placa ao produtor com semanas
 * de placa de venda numa média só produziria um número sem unidade definida —
 * meia porcentagem de uma base e meia de outra. O rodapé escreve uma frase por
 * grupo justamente para que cada número diga contra o que foi medido.
 */
export interface MediaPorReferencialDTO {
  referencial: string;
  /** Quantas semanas do período entraram nesta média. */
  semanas: number;
  /** A média SIMPLES das diferenças daquelas semanas, na forma do referencial. */
  media: ComparacaoDTO;
}

export interface MinhasPorSemanaDTO {
  variedade: string;
  qualidade: string | null;
  label: string;
  periodo: PeriodoHistorico;
  semanas: MinhaSemanaDTO[];
  /** No máximo duas entradas; grupo sem semana comparável não aparece. */
  mediasPorReferencial: MediaPorReferencialDTO[];
  itensNaoMapeados: number;
}

/**
 * As chaves que os DTOs do PRODUTOR NUNCA podem carregar — o mesmo teste de
 * contrato de `shared/mercado.ts`. Cotação é preço; custo, diária e Manejo não
 * têm o que fazer aqui, e "vazou junto" não pode ser acidente de spread.
 */
export const CHAVES_PROIBIDAS_COTACOES = [
  "custo",
  "custoKg",
  "diaria",
  "diarias",
  "margem",
  "lucro",
  "orgId",
  "operacao",
  "operacoes",
  "insumo",
  "insumos",
  "doseHa",
] as const;

// ─── Admin (usuário de plataforma) ───────────────────────────────────────────

export interface LinhaAdminCotacaoDTO {
  id: string;
  variedade: string;
  qualidade: string | null;
  label: string;
  precoKg: number;
  data: string;
  /** ISO "AAAA-MM-DD" — o campo "válida até" do formulário do admin. */
  validaAte: string;
  referencial: string;
  fonte: string | null;
  observacao: string | null;
  atualizadoEm: string | null;
}

export interface AdminCotacoesDTO {
  praca: string;
  data: string;
  /**
   * O referencial que o formulário abre selecionado: o da publicação DAQUELE
   * dia, se houver, senão o da última publicação da praça. Sem histórico
   * nenhum, `null` — e aí o admin escolhe, porque adivinhar seria carimbar uma
   * placa que ninguém leu.
   */
  referencialSugerido: string | null;
  /** As linhas daquela data (vazio se nada publicado nela). */
  linhas: LinhaAdminCotacaoDTO[];
  /**
   * A ÚLTIMA cotação publicada de cada produto, seja qual for a data — é o que
   * pré-preenche o formulário e o que dá o delta "▲ 3,4% vs 03/09".
   */
  ultimas: LinhaAdminCotacaoDTO[];
  /** Todo o histórico da praça, mais recente em cima (a tabela de baixo). */
  historico: DiaAdminCotacaoDTO[];
}

/** Uma data do histórico do admin: os quatro produtos em colunas. */
export interface DiaAdminCotacaoDTO {
  data: string;
  /** A validade daquele dia (a menor entre as linhas). */
  validaAte: string | null;
  /** O chip "Divulgado como" do histórico. */
  referencial: string;
  fonte: string | null;
  /** `chaveProduto()` → preço. Produto não publicado naquele dia fica de fora. */
  precos: Record<string, number>;
  /** Ids das linhas daquele dia — o botão Excluir precisa deles. */
  ids: string[];
}

// ─── Delta ───────────────────────────────────────────────────────────────────

/**
 * Variação percentual entre dois preços.
 *
 * `null` quando não há base de comparação — **nunca `0`**. Zero é "o preço não
 * mudou"; ausência é "não havia preço na semana passada", e a tela desenha as
 * duas coisas de jeitos diferentes (`= 0,0%` contra `—`). Confundi-las já é o
 * motivo de o componente `Delta` tratar `null` explicitamente.
 */
export function delta(atual: number | null | undefined, anterior: number | null | undefined): number | null {
  if (atual == null || anterior == null || !isFinite(atual) || !isFinite(anterior) || anterior === 0) return null;
  return ((atual - anterior) / anterior) * 100;
}

/**
 * A FORMA da comparação com a praça — decidida pelo referencial (059).
 *
 * `PERCENTUAL` só quando as duas pontas medem a mesma coisa (placa **ao
 * produtor** × o que o produtor recebeu): aí "4% abaixo" é uma frase
 * verdadeira. Contra uma placa **de venda** as bases não batem — uma é a caixa
 * embalada na mão do comprador, a outra é o pé da carga — e a única coisa
 * honesta de dizer é a diferença em R$/kg, rotulada como diferença PARA A
 * PLACA. Nunca "vendeu abaixo da praça".
 */
export type TipoComparacao = "PERCENTUAL" | "ABSOLUTO";

export interface ComparacaoDTO {
  tipo: TipoComparacao;
  /** % quando `PERCENTUAL`; R$/kg (meu − placa) quando `ABSOLUTO`. */
  valor: number;
}

/**
 * `meuPreco` contra a praça, na forma que o referencial permite. `null` quando
 * falta uma das pontas — **nunca** `0`, pela mesma razão de `delta`.
 *
 * ⚠️ Não existe conversão. Esta função escolhe a FORMA de mostrar; ela não
 * transforma preço de venda em preço ao produtor nem o contrário, e nenhum
 * desconto entra aqui.
 */
export function compararComPraca(
  meuPreco: number | null | undefined,
  precoPraca: number | null | undefined,
  referencial: string,
): ComparacaoDTO | null {
  if (referencial === "VENDA") {
    if (meuPreco == null || precoPraca == null || !isFinite(meuPreco) || !isFinite(precoPraca)) return null;
    return { tipo: "ABSOLUTO", valor: meuPreco - precoPraca };
  }
  const pct = delta(meuPreco, precoPraca);
  return pct == null ? null : { tipo: "PERCENTUAL", valor: pct };
}
